import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import TestRunModal from './TestRunModal';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const DigitalQaProject = ({ onLogout }) => {
  const { projectName } = useParams();
  const [projectInfo, setProjectInfo] = useState(null);
  const [testRuns, setTestRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTestRuns, setLoadingTestRuns] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cancellingPipelineId, setCancellingPipelineId] = useState(null);
  const [toast, setToast] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailsModal, setDetailsModal] = useState({ isOpen: false, pipelineInfo: null });

  const fetchTestRuns = useCallback(async (page) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setLoadingTestRuns(true);
    try {
      const response = await axios.get(`${API_URL}/digital-qa/test-runs/${projectName}?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTestRuns(response.data.pipelines || []);
      setCurrentPage(response.data.page || 1);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) { console.error('Failed to fetch test runs:', err); }
    finally { setLoadingTestRuns(false); }
  }, [projectName]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) { setError('Not authenticated'); setLoading(false); return; }
      try {
        const [projectInfoResponse, testRunsResponse] = await Promise.all([
          axios.get(`${API_URL}/digital-qa/project-info/${projectName}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/digital-qa/test-runs/${projectName}?page=1`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setProjectInfo(projectInfoResponse.data);
        setTestRuns(testRunsResponse.data.pipelines || []);
        setCurrentPage(testRunsResponse.data.page || 1);
        setTotalPages(testRunsResponse.data.totalPages || 1);
        setLoading(false);
      } catch (err) { setError(err.response?.data?.message || 'Failed to load project data'); setLoading(false); }
    };
    if (projectName) fetchData();
  }, [projectName]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
      });
    } catch (e) { return dateString; }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleCancelPipeline = async (pipelineId) => {
    const token = localStorage.getItem('authToken');
    if (!token) { showToast('Not authenticated', 'error'); return; }
    setCancellingPipelineId(pipelineId);
    try {
      const response = await axios.post(`${API_URL}/digital-qa/pipelines/${projectName}/${pipelineId}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showToast(response.data.success ? 'Pipeline cancelled successfully' : (response.data.message || 'Failed to cancel pipeline'), response.data.success ? 'success' : 'error');
      fetchTestRuns(currentPage);
    } catch (err) { showToast(err.response?.data?.message || 'Failed to cancel pipeline', 'error'); }
    finally { setCancellingPipelineId(null); }
  };

  const handleRunStarted = () => { showToast('Test run started successfully', 'success'); fetchTestRuns(currentPage); };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'success': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'failed': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'running': case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-96"><LoadingSpinner /></div>;

  if (error) return (
    <div className="p-4">
      <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">{error}</div>
    </div>
  );

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{projectInfo?.title || projectName}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Digital QA Test Management</p>
      </div>

      {/* Start Test Button */}
      {projectName !== 'vmap' && (
        <div className="mb-4">
          <button onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Start New Test Run
          </button>
        </div>
      )}

      {/* Test Runs Table */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Test Runs</h2>
        </div>

        {loadingTestRuns ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>
        ) : testRuns.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">No test runs available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3">Run ID</th>
                  <th className="px-4 py-3">Start Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {testRuns.map((run) => (
                  <tr key={run.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{run.id}</td>
                    <td className="px-4 py-3">{formatDate(run.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(run.status)}`}>
                        {run.status?.toLowerCase() === 'success' ? 'Finished' : run.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {run.status?.toLowerCase() === 'running' && (
                        <button onClick={() => handleCancelPipeline(run.id)} disabled={cancellingPipelineId === run.id}
                          className="font-medium text-red-600 hover:text-red-800 dark:text-red-500 dark:hover:text-red-400 disabled:opacity-50">
                          {cancellingPipelineId === run.id ? 'Cancelling...' : 'Cancel'}
                        </button>
                      )}
                      {run.status?.toLowerCase() === 'success' && run.pipelineInfo && (
                        <button onClick={() => setDetailsModal({ isOpen: true, pipelineInfo: run.pipelineInfo })}
                          className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-400">
                          View Details
                        </button>
                      )}
                      {!(run.status?.toLowerCase() === 'running') && !(run.status?.toLowerCase() === 'success' && run.pipelineInfo) && (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t dark:border-gray-700">
            <button onClick={() => { if (currentPage > 1) fetchTestRuns(currentPage - 1); }} disabled={currentPage <= 1}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-50">
              Previous
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-400">
              Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
            </span>
            <button onClick={() => { if (currentPage < totalPages) fetchTestRuns(currentPage + 1); }} disabled={currentPage >= totalPages}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white disabled:opacity-50">
              Next
            </button>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 p-4 rounded-lg shadow-lg text-sm ${
          toast.type === 'success' ? 'text-green-800 bg-green-50 dark:bg-green-800 dark:text-green-200' : 'text-red-800 bg-red-50 dark:bg-red-800 dark:text-red-200'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Test Run Modal */}
      <TestRunModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}
        projectName={projectName} projectTitle={projectInfo?.title || projectName} onRunStarted={handleRunStarted} />

      {/* Details Modal */}
      {detailsModal.isOpen && detailsModal.pipelineInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/50 dark:bg-gray-900/80" onClick={() => setDetailsModal({ isOpen: false, pipelineInfo: null })}>
          <div className="relative w-full max-w-3xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-white rounded-lg shadow dark:bg-gray-800 max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b dark:border-gray-600 flex-shrink-0">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Pipeline Details</h3>
                <button onClick={() => setDetailsModal({ isOpen: false, pipelineInfo: null })} className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" /></svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b dark:border-gray-700">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Start Date</span>
                    <span className="text-sm text-gray-900 dark:text-white">{formatDate(detailsModal.pipelineInfo.start_date)}</span>
                  </div>
                  {detailsModal.pipelineInfo.test_run_id && (
                    <div className="flex justify-between py-2 border-b dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Test Run ID</span>
                      <span className="text-sm text-gray-900 dark:text-white">{detailsModal.pipelineInfo.test_run_id}</span>
                    </div>
                  )}
                  {detailsModal.pipelineInfo.test_case_ids?.length > 0 && (
                    <div className="flex justify-between py-2 border-b dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Test Case IDs</span>
                      <span className="text-sm text-gray-900 dark:text-white">{detailsModal.pipelineInfo.test_case_ids.join(', ')}</span>
                    </div>
                  )}
                  {detailsModal.pipelineInfo.allure_report_url && (
                    <div className="flex justify-between py-2 border-b dark:border-gray-700">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Allure Report</span>
                      <a href={detailsModal.pipelineInfo.allure_report_url} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-medium text-primary-600 hover:text-primary-800 dark:text-primary-500">View Report</a>
                    </div>
                  )}
                  {detailsModal.pipelineInfo.error && (
                    <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-700 dark:text-red-400">{detailsModal.pipelineInfo.error}</div>
                  )}
                </div>

                {detailsModal.pipelineInfo.test_data && Object.keys(detailsModal.pipelineInfo.test_data).length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Test Results</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                          <tr>
                            <th className="px-4 py-3">Test Case ID</th>
                            <th className="px-4 py-3">Duration</th>
                            <th className="px-4 py-3">Cost (USD)</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(detailsModal.pipelineInfo.test_data).map(([testCaseId, data]) => {
                            const isFailed = data.error || data.test_report?.status === 'failed' || data.test_report?.status === 'broken';
                            const errorMessage = data.error || data.test_report?.statusDetails?.message;
                            return (
                              <React.Fragment key={testCaseId}>
                                <tr className="border-b dark:border-gray-700">
                                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{testCaseId}</td>
                                  <td className="px-4 py-3">{data.duration_ms ? `${(data.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                                  <td className="px-4 py-3">{data.total_cost_usd ? `$${data.total_cost_usd.toFixed(4)}` : '—'}</td>
                                  <td className="px-4 py-3">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isFailed ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'}`}>
                                      {isFailed ? 'Failed' : 'Passed'}
                                    </span>
                                  </td>
                                </tr>
                                {errorMessage && (
                                  <tr className="border-b dark:border-gray-700">
                                    <td colSpan="4" className="px-4 py-2">
                                      <div className="p-2 text-xs text-red-800 rounded bg-red-50 dark:bg-gray-700 dark:text-red-400">{errorMessage}</div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DigitalQaProject;
